import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["day", "brush", "reasonModal", "form", "undoButton"]
  static values = {
    activeLevel: { type: String, default: "willing" },
    mobile: Boolean,
    periodId: String,
    validationMessage: { type: String, default: "Vul een reden in voor alle geblokkeerde dagen." },
    reasonPrompt: { type: String, default: "Reden voor blokkade op" }
  }

  connect() {
    this.painting = false
    this.undoStack = []
    this.restoreFormState()
    this.updateBrushStyles()
  }

  selectBrush(event) {
    this.activeLevelValue = event.currentTarget.dataset.level
    this.updateBrushStyles()
  }

  updateBrushStyles() {
    this.brushTargets.forEach(btn => {
      const isActive = btn.dataset.level === this.activeLevelValue
      btn.classList.toggle("ring-2", isActive)
      btn.classList.toggle("ring-offset-2", isActive)
      btn.classList.toggle("ring-gray-900", isActive)
    })
  }

  // --- Mouse events (desktop) ---
  startPainting(event) {
    event.preventDefault()
    this.painting = true
    this.saveSnapshot()
    this.paint(event)
  }

  continuePainting(event) {
    if (this.painting) this.paint(event)
  }

  stopPainting() {
    this.painting = false
    this.persistFormState()
  }

  // --- Touch events (tablet/mobile) ---
  startPaintingTouch(event) {
    event.preventDefault()
    this.painting = true
    this.saveSnapshot()
    const touch = event.touches[0]
    const el = document.elementFromPoint(touch.clientX, touch.clientY)
    if (el && el.dataset.preferenceCalendarTarget === "day") {
      this.applyPaint(el)
    }
  }

  continuePaintingTouch(event) {
    if (!this.painting) return
    event.preventDefault()
    const touch = event.touches[0]
    const el = document.elementFromPoint(touch.clientX, touch.clientY)
    if (el && el.dataset.preferenceCalendarTarget === "day") {
      this.applyPaint(el)
    }
  }

  stopPaintingTouch() {
    this.painting = false
    this.persistFormState()
  }

  paint(event) {
    const day = event.currentTarget
    if (day.dataset.preferenceCalendarTarget === "day") {
      this.applyPaint(day)
    }
  }

  applyPaint(day) {
    day.dataset.level = this.activeLevelValue
    this.updateVisual(day)
    this.updateHiddenInput(day)

    if (this.activeLevelValue === "blocked") {
      this.showReasonInput(day)
    }
  }

  // --- fillWeekday with toggle-undo ---
  fillWeekday(event) {
    this.saveSnapshot()
    const weekday = event.currentTarget.dataset.weekday
    const targets = this.dayTargets.filter(d => d.dataset.weekday === weekday)
    const allSameLevel = targets.every(d => d.dataset.level === this.activeLevelValue)

    targets.forEach(d => {
      d.dataset.level = allSameLevel ? "willing" : this.activeLevelValue
      this.updateVisual(d)
      this.updateHiddenInput(d)
    })
    this.persistFormState()
  }

  // --- Undo (Ctrl+Z or button) ---
  undo() {
    const snapshot = this.undoStack.pop()
    if (!snapshot) return

    this.dayTargets.forEach((d, i) => {
      if (snapshot[i]) {
        d.dataset.level = snapshot[i].level
        d.dataset.reason = snapshot[i].reason || ""
        this.updateVisual(d)
        this.updateHiddenInput(d)
      }
    })
    this.persistFormState()
  }

  saveSnapshot() {
    const snap = this.dayTargets.map(d => ({
      level: d.dataset.level || "willing",
      reason: d.dataset.reason || ""
    }))
    this.undoStack.push(snap)
    if (this.undoStack.length > 50) this.undoStack.shift()
  }

  // --- Client-side form state preservation ---
  persistFormState() {
    const state = this.dayTargets.map(d => ({
      date: d.dataset.date,
      level: d.dataset.level || "willing",
      reason: d.dataset.reason || ""
    }))
    sessionStorage.setItem(
      `pref_calendar_${this.periodIdValue}`,
      JSON.stringify(state)
    )
  }

  restoreFormState() {
    const saved = sessionStorage.getItem(`pref_calendar_${this.periodIdValue}`)
    if (!saved) return

    try {
      const state = JSON.parse(saved)
      state.forEach(s => {
        const day = this.dayTargets.find(d => d.dataset.date === s.date)
        if (day) {
          day.dataset.level = s.level
          day.dataset.reason = s.reason || ""
          this.updateVisual(day)
          this.updateHiddenInput(day)
        }
      })
    } catch (e) {
      // Ignore corrupt state
    }
  }

  // --- Client-side validation ---
  validateBeforeSubmit(event) {
    const blockedWithoutReason = this.dayTargets.filter(d =>
      d.dataset.level === "blocked" && !(d.dataset.reason || "").trim()
    )

    if (blockedWithoutReason.length > 0) {
      event.preventDefault()
      const msg = this.validationMessageValue
      alert(msg)
      blockedWithoutReason[0].scrollIntoView({ behavior: "smooth" })
    } else {
      sessionStorage.removeItem(`pref_calendar_${this.periodIdValue}`)
    }
  }

  // --- Visual helpers ---
  updateVisual(day) {
    const level = day.dataset.level || "willing"
    const colors = {
      eager: "bg-green-500 text-white",
      willing: "bg-green-100 text-green-900",
      reluctant: "bg-orange-200 text-orange-900",
      blocked: "bg-red-500 text-white"
    }

    day.className = day.className
      .replace(/bg-\S+/g, "")
      .replace(/text-\S+/g, "")
      .trim()

    const classes = (colors[level] || colors.willing).split(" ")
    classes.forEach(c => day.classList.add(c))
  }

  updateHiddenInput(day) {
    const date = day.dataset.date
    const level = day.dataset.level || "willing"
    const reason = day.dataset.reason || ""

    const levelInput = this.element.querySelector(`input[name="date_preferences[${date}][level]"]`)
    const reasonInput = this.element.querySelector(`input[name="date_preferences[${date}][reason]"]`)

    if (levelInput) levelInput.value = level
    if (reasonInput) reasonInput.value = reason
  }

  showReasonInput(day) {
    const date = day.dataset.date
    const reason = prompt(this.reasonPromptValue + " " + date + ":")
    if (reason) {
      day.dataset.reason = reason
      const reasonInput = this.element.querySelector(`input[name="date_preferences[${date}][reason]"]`)
      if (reasonInput) reasonInput.value = reason
    }
  }
}
